import { useEffect, useRef, useState } from 'react';
import type { PlayerSolutionDTO, SubmitSolutionRequest, SubmitSolutionResult } from '@shared/api.js';
import { modeAllowsImage, modeAllowsText, SOLUTION_STATUS_LABELS, SOLUTION_TEXT_MAX } from '@shared/solution.js';
import { api, ApiError, NetworkError } from '../../lib/apiClient.js';
import { newId } from '../../lib/id.js';
import { gameTimeZone } from '../../lib/gameTime.js';
import { uploadImage } from '../../lib/uploadImage.js';
import { TerminalShell } from '../terminal/TerminalShell.js';
import { CameraCapture } from '../shared/CameraCapture.js';

function formatTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('he-IL', {
      timeZone: gameTimeZone(),
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function defaultPrompt(mode: PlayerSolutionDTO['mode']): string {
  if (mode === 'image') return 'כדי להמשיך למשימה הבאה — צלמו והעלו תמונה כפתרון.';
  if (mode === 'text') return 'כדי להמשיך למשימה הבאה — כתבו את הפתרון.';
  return 'כדי להמשיך למשימה הבאה — העלו תמונה או כתבו את הפתרון.';
}

/** הסיבה שהשרת נתן לדחייה (reason) או הודעת רשת/שרת קריאה */
function failureReason(e: unknown): string {
  if (e instanceof NetworkError) return 'אין חיבור — נסו שוב כשתהיה קליטה';
  if (e instanceof ApiError) {
    const body = e.body as { reason?: string; error?: string } | null;
    return body?.reason ?? body?.error ?? e.message;
  }
  return 'השליחה נכשלה — נסו שוב';
}

interface Attempt {
  id: string;
  text: string;
  blob: Blob | null;
  imageUrl: string | null;
}

/**
 * הגשת פתרון למשימה (תמונה ו/או טקסט). המשימה הבאה נפתחת ישירות ברגע שהפתרון מאושר — אוטומטית אם הוא טקסט
 * שמתאים לתשובה שהוגדרה, אחרת באישור המפקדה. ההגשה אידמפוטנטית (אותו מזהה בניסיון חוזר), והתמונה שכבר
 * הועלתה לא מועלית שוב כשהשליחה עצמה נכשלה ברשת.
 */
export function SolutionPanel({
  solution,
  online,
  animationsEnabled,
  onChanged,
}: {
  solution: PlayerSolutionDTO;
  online: boolean;
  animationsEnabled: boolean;
  onChanged: () => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [image, setImage] = useState<{ blob: Blob; previewUrl: string } | null>(null);
  const [sending, setSending] = useState(false);
  // ref = "מזהה:סטטוס" של ההגשה שההודעה מתארת — כשהסטטוס משתנה (למשל המנהל דחה) ההודעה הישנה כבר לא מוצגת
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string; ref: string | null } | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const attemptRef = useRef<Attempt | null>(null);

  const allowImage = modeAllowsImage(solution.mode);
  const allowText = modeAllowsText(solution.mode);
  const latest = solution.submissions[0] ?? null;
  const waiting = latest?.status === 'pending';
  // התמונה נבדקת אוטומטית כרגע — התשובה מגיעה בעוד כמה שניות (המסך מתרענן מעצמו), ואין טעם להגיש שוב
  const checking = waiting && latest?.checking === true;
  const showForm = !solution.approved && (!waiting || (replacing && !checking));

  // מעבר למשימה אחרת — מתחילים טופס נקי
  useEffect(() => {
    setText('');
    setMessage(null);
    setReplacing(false);
    attemptRef.current = null;
  }, [solution.missionId]);

  useEffect(
    () => () => {
      if (image) URL.revokeObjectURL(image.previewUrl);
    },
    [image],
  );

  const pickImage = (blob: Blob) => setImage({ blob, previewUrl: URL.createObjectURL(blob) });
  const clearImage = () => {
    setImage(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    const trimmed = text.trim();
    if ((!trimmed && !image) || sending) return;
    setSending(true);
    setMessage(null);
    try {
      let attempt = attemptRef.current;
      if (!attempt || attempt.text !== trimmed || attempt.blob !== (image?.blob ?? null)) {
        attempt = { id: newId(), text: trimmed, blob: image?.blob ?? null, imageUrl: null };
        attemptRef.current = attempt;
      }
      if (image && !attempt.imageUrl) attempt.imageUrl = await uploadImage(image.blob);
      const req: SubmitSolutionRequest = {
        id: attempt.id,
        missionId: solution.missionId,
        text: trimmed,
        imageUrl: attempt.imageUrl,
      };
      const result = await api.post<SubmitSolutionResult>('/api/player/solutions', req);
      if (!result.ok) throw new ApiError(result.reason, 400, result);
      attemptRef.current = null;
      setText('');
      clearImage();
      setReplacing(false);
      const { id, status, note, checking: nowChecking } = result.submission;
      const ref = `${id}:${status}`;
      setMessage(
        status === 'approved'
          ? { kind: 'ok', text: '✓ הפתרון אושר!', ref }
          : status === 'rejected'
            ? { kind: 'error', text: `✗ ${note || 'הפתרון לא התקבל — נסו שוב'}`, ref }
            : nowChecking
              ? { kind: 'ok', text: '🔍 התמונה נשלחה — בודקים אותה עכשיו', ref }
              : { kind: 'ok', text: '⏳ הפתרון נשלח — ממתין לאישור המפקדה', ref },
      );
      await onChanged();
    } catch (e) {
      setMessage({ kind: 'error', text: `⚠️ ${failureReason(e)}`, ref: null });
    } finally {
      setSending(false);
    }
  };

  const statusLabel = solution.approved ? 'אושר ✓' : checking ? 'בבדיקה...' : waiting ? 'ממתין לאישור' : 'נדרש';
  const latestRef = latest ? `${latest.id}:${latest.status}` : null;
  const ownMessage = message && (message.ref === null || message.ref === latestRef) ? message : null;
  // דחייה (גם אוטומטית, שמגיעה בזמן שהשחקן מסתכל במסך) מוצגת בולטת בראש הטופס, לא רק בהיסטוריה
  const visibleMessage =
    ownMessage ??
    (latest?.status === 'rejected' && !solution.approved && !sending
      ? { kind: 'error' as const, text: `✗ ${latest.note || 'הפתרון לא התקבל — נסו שוב'}`, ref: latestRef }
      : null);

  return (
    <TerminalShell
      animationsEnabled={animationsEnabled}
      ticker="◤ SOLUTION UPLINK ◢ VERIFICATION REQUIRED ◢ AWAITING PROOF ◢"
      className="solution-panel"
      header={
        <div className="term-panel-header">
          <span>פתרון נדרש{solution.missionNumber !== null ? ` — משימה #${solution.missionNumber}` : ''}</span>
          <span>{statusLabel}</span>
        </div>
      }
    >
      {cameraOpen && (
        <CameraCapture
          onCapture={(blob) => {
            pickImage(blob);
            setCameraOpen(false);
          }}
          onCancel={() => setCameraOpen(false)}
        />
      )}

      <div className="solution-panel__body">
        <div className="solution-panel__prompt term-text">{solution.prompt || defaultPrompt(solution.mode)}</div>

        {solution.approved && (
          <div className="solution-panel__approved" role="status">
            ✓ הפתרון אושר — המשימה הבאה בדרך
          </div>
        )}

        {solution.aiCheck && allowImage && !solution.approved && !waiting && (
          <div className="solution-panel__hint">🤖 התמונה נבדקת אוטומטית — התשובה מגיעה תוך כמה שניות</div>
        )}

        {checking && (
          <div className="solution-panel__checking" role="status">
            <span className="solution-panel__spinner" aria-hidden="true" />
            בודקים את התמונה… התשובה מגיעה תוך כמה שניות
          </div>
        )}

        {waiting && !checking && !replacing && (
          <div className="solution-panel__waiting" role="status">
            ⏳ ההגשה נשלחה וממתינה לאישור המפקדה. המשימה הבאה תיפתח ברגע שתאושר.
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setReplacing(true)}>
              להגיש מחדש
            </button>
          </div>
        )}

        {showForm && (
          <div className="solution-panel__form">
            {image && (
              <div className="field-notes__preview">
                <img src={image.previewUrl} alt="תצוגה מקדימה של הפתרון" />
                <button type="button" className="field-notes__preview-remove" onClick={clearImage} aria-label="הסר תמונה">
                  ✕
                </button>
              </div>
            )}
            <div className="field-notes__row">
              {allowImage && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="field-notes__file-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) pickImage(file);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setCameraOpen(true)}
                    aria-label="צלם עכשיו"
                    title="צלם עכשיו"
                  >
                    📸 צלם
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => fileRef.current?.click()}
                    aria-label="בחר תמונה מהגלריה"
                    title="בחר מהגלריה"
                  >
                    📷 גלריה
                  </button>
                </>
              )}
              {allowText && (
                <input
                  type="text"
                  className="field-notes__input"
                  value={text}
                  placeholder={allowImage ? 'או כתבו את הפתרון...' : 'כתבו את הפתרון...'}
                  maxLength={SOLUTION_TEXT_MAX}
                  disabled={sending}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submit();
                  }}
                />
              )}
              <button
                type="button"
                className="btn btn--sm btn--primary"
                disabled={sending || !online || (!text.trim() && !image)}
                onClick={() => void submit()}
              >
                {sending ? 'שולח...' : 'שלח פתרון'}
              </button>
            </div>
            {!online && <div className="field-notes__status">⚠️ אין חיבור — אפשר לשלוח כשתהיה קליטה</div>}
          </div>
        )}

        {visibleMessage && (
          <div className={`solution-panel__message solution-panel__message--${visibleMessage.kind}`} role="status">
            {visibleMessage.text}
          </div>
        )}

        {solution.submissions.length > 0 && (
          <ul className="solution-panel__history">
            {solution.submissions.slice(0, 4).map((s) => (
              <li key={s.id} className={`solution-entry solution-entry--${s.status}`}>
                <div className="solution-entry__head">
                  <span className="solution-entry__status">{s.checking ? 'בבדיקה...' : SOLUTION_STATUS_LABELS[s.status]}</span>
                  <span>{formatTime(s.submittedAt)}</span>
                </div>
                {s.text && (
                  <div className="solution-entry__text" dir="auto">
                    {s.text}
                  </div>
                )}
                {s.imageUrl && <img src={s.imageUrl} alt="הפתרון שנשלח" className="solution-entry__image" />}
                {s.note && <div className="solution-entry__note">{s.note}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </TerminalShell>
  );
}
